import { useState, useEffect } from 'react';
import { AnimPopInStack } from '@readdy/anim/card/pop-in-stack/react';

interface HeroProps {
  onBook: () => void;
  onWechat: () => void;
}

export default function Hero({ onBook, onWechat }: HeroProps) {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[560px] md:min-h-[100dvh] w-full overflow-hidden">
        {/* Scaled background image for immersive desktop view */}
        <div
          className="absolute inset-[-10%] w-[120%] h-[120%] bg-cover bg-center scale-105 md:scale-110 transition-transform duration-700"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=Luxurious%20dark%20moody%20still%20life%20of%20a%20premium%20Cuban%20cigar%20resting%20on%20a%20crystal%20ashtray%20beside%20a%20half-filled%20glass%20of%20deep%20ruby%20red%20wine%2C%20wooden%20humidor%20and%20aged%20oak%20barrel%20in%20background%2C%20warm%20golden%20candle%20light%2C%20soft%20smoke%20curling%20upward%2C%20editorial%20cinematic%20photography%2C%20rich%20mahogany%20and%20amber%20tones%2C%20intimate%20lounge%20atmosphere%2C%20high%20detail&width=1600&height=1000&seq=cigar-hero-01&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso-900/70 via-espresso-900/40 to-espresso-900/20" />

        {/* animated smoke */}
        <div className="pointer-events-none absolute bottom-24 left-8 w-24 h-24 md:w-32 md:h-32 rounded-full bg-cream-50/20 blur-2xl animate-smoke" />
        <div className="pointer-events-none absolute bottom-32 right-10 w-16 h-16 md:w-24 md:h-24 rounded-full bg-cream-50/15 blur-2xl animate-smoke" style={{ animationDelay: "2s" }} />

        <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-6 pt-28 md:pt-36 text-center">
          <div className="flex items-center gap-3 text-gold-300 animate-fade-up">
            <span className="h-px w-10 md:w-14 bg-gold-400 animate-shimmer" />
            <span className="font-serif tracking-[0.4em] text-xs md:text-sm uppercase">AOC Cigar Club · Since 2016</span>
            <span className="h-px w-10 md:w-14 bg-gold-400 animate-shimmer" />
          </div>

          <h1 className="mt-6 md:mt-8 font-display text-5xl md:text-7xl lg:text-8xl font-medium text-cream-50 leading-tight animate-fade-up-delay-1">
            百汇茄社
          </h1>
          <p className="mt-4 font-sc text-lg md:text-xl lg:text-2xl text-gold-300 tracking-widest animate-fade-up-delay-1">
            雪茄 · 红酒 · 典雅
          </p>

          <p className="mt-6 md:mt-8 max-w-lg lg:max-w-xl font-sc text-sm md:text-base lg:text-lg text-cream-100/85 leading-relaxed animate-fade-up-delay-2">
            ·源自古巴哈瓦那的手工雪茄，与波尔多五大名庄红酒相佐。一口一啜之间，是绅士的雅趣，也是时光的沉淀。正品古巴雪茄，首选百汇茄社·
          </p>

          <div className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center gap-3 animate-fade-up-delay-3">
            <button
              onClick={onWechat}
              className="cursor-pointer whitespace-nowrap group inline-flex items-center gap-2 rounded-md bg-gold-400 hover:bg-gold-500 transition-colors text-espresso-900 font-sc font-medium px-7 md:px-9 py-3.5 md:py-4 text-sm md:text-base shadow-none"
            >
              <img
                src="https://storage.readdy-site.link/project_files/d891e450-b88b-4862-a138-b46b8b4d4b90/761fc049-2216-4ca5-a151-41cabdad724c_compressed_wx2.webp"
                alt="微信"
                className="w-7 h-7 object-contain"
              />
              <span>添加雪茄顾问</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>

          {reducedMotion ? (
            <div className="mt-12 md:mt-16 grid grid-cols-3 gap-6 md:gap-10 lg:gap-14 text-cream-100/80">
              <div className="text-center">
                <div className="font-display text-2xl md:text-3xl lg:text-4xl text-gold-300">27<span className="text-sm md:text-base">Yr</span></div>
                <div className="mt-1 text-[11px] md:text-xs lg:text-sm font-sc tracking-widest">匠心传承</div>
              </div>
              <div className="text-center border-x border-gold-300/20">
                <div className="font-display text-2xl md:text-3xl lg:text-4xl text-gold-300">120<span className="text-sm md:text-base">+</span></div>
                <div className="mt-1 text-[11px] md:text-xs lg:text-sm font-sc tracking-widest">古巴雪茄</div>
              </div>
              <div className="text-center">
                <div className="font-display text-2xl md:text-3xl lg:text-4xl text-gold-300">3800<span className="text-sm md:text-base">+</span></div>
                <div className="mt-1 text-[11px] md:text-xs lg:text-sm font-sc tracking-widest">私享会员</div>
              </div>
            </div>
          ) : (
            <AnimPopInStack distance={12} duration={600}>
              <div className="mt-12 md:mt-16 grid grid-cols-3 gap-6 md:gap-10 lg:gap-14 text-cream-100/80">
                <div className="text-center">
                  <div className="font-display text-2xl md:text-3xl lg:text-4xl text-gold-300">27<span className="text-sm md:text-base">Yr</span></div>
                  <div className="mt-1 text-[11px] md:text-xs lg:text-sm font-sc tracking-widest">匠心传承</div>
                </div>
                <div className="text-center border-x border-gold-300/20">
                  <div className="font-display text-2xl md:text-3xl lg:text-4xl text-gold-300">120<span className="text-sm md:text-base">+</span></div>
                  <div className="mt-1 text-[11px] md:text-xs lg:text-sm font-sc tracking-widest">古巴雪茄</div>
                </div>
                <div className="text-center">
                  <div className="font-display text-2xl md:text-3xl lg:text-4xl text-gold-300">3800<span className="text-sm md:text-base">+</span></div>
                  <div className="mt-1 text-[11px] md:text-xs lg:text-sm font-sc tracking-widest">私享会员</div>
                </div>
              </div>
            </AnimPopInStack>
          )}
        </div>
      </div>
    </section>
  );
}